import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Upload, 
  FileText, 
  Loader2, 
  AlertCircle, 
  FlaskConical,
  Droplets,
  Heart,
  Microscope,
  TestTube,
  Activity
} from "lucide-react";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Progress } from "../../components/ui/progress";
import { toast } from "sonner";
import axios from "axios";
import { LabsLayout } from "../../components/LabsLayout";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const reportCategories = [
  { icon: Droplets, label: "Blood Tests", examples: "CBC, LFT, KFT, Lipid Profile" },
  { icon: TestTube, label: "Urine & Stool", examples: "R/E, Culture, ACR" },
  { icon: Heart, label: "Cardiac Tests", examples: "ECG, ECHO, Troponin" },
  { icon: Activity, label: "Thyroid & Hormones", examples: "TSH, T3/T4, HbA1c" },
  { icon: Microscope, label: "Cancer Markers", examples: "PSA, CA-125, CEA, AFP" },
  { icon: FlaskConical, label: "Infectious", examples: "HIV, HBsAg, RT-PCR" },
];

export default function LabReportsPage() {
  const navigate = useNavigate();
  const [isDragging, setIsDragging] = useState(false);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [progress, setProgress] = useState(0);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    handleFileSelect(droppedFile);
  }, []);

  const handleFileSelect = (selectedFile) => {
    if (!selectedFile) return;

    const validTypes = ["image/jpeg", "image/png", "image/webp", "application/pdf"];
    if (!validTypes.some(type => selectedFile.type.includes(type.split("/")[1]))) {
      toast.error("Invalid file type. Please upload JPEG, PNG, WEBP images or PDF files.");
      return;
    }

    setFile(selectedFile);

    if (selectedFile.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = (e) => setPreview(e.target.result);
      reader.readAsDataURL(selectedFile);
    } else {
      setPreview(null);
    }
  };

  const handleAnalyze = async () => {
    if (!file) {
      toast.error("Please select a file first");
      return;
    }

    setIsAnalyzing(true);
    setProgress(0);

    const progressInterval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 90) return prev;
        return prev + Math.random() * 15;
      });
    }, 500);

    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await axios.post(`${API}/labs/analyze-lab-report`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        withCredentials: true
      });

      setProgress(100);
      clearInterval(progressInterval);

      toast.success("Analysis complete!");
      
      setTimeout(() => {
        navigate(`/labs/lab-report/${response.data.id}`);
      }, 500);

    } catch (error) {
      clearInterval(progressInterval);
      console.error("Analysis error:", error);
      toast.error(error.response?.data?.detail || "Analysis failed. Please try again.");
    } finally {
      setIsAnalyzing(false);
      setProgress(0);
    }
  };

  const clearFile = () => {
    setFile(null);
    setPreview(null);
  };

  return (
    <LabsLayout>
      <div className="min-h-[calc(100vh-4rem)]">
        {/* Hero Section */}
        <section className="relative py-16 md:py-20 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-emerald-950/20 to-transparent" />
          
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/20 mb-6">
                <FlaskConical className="w-4 h-4 text-emerald-500" />
                <span className="text-sm font-medium text-emerald-400">Laboratory Report Analysis</span>
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
                AI-Powered
                <span className="text-purple-400"> Lab Report</span> Analysis
              </h1>
              <p className="text-lg md:text-xl text-zinc-400 max-w-2xl mx-auto leading-relaxed">
                Upload your blood tests, urine analysis, cardiac reports, or any laboratory results 
                for instant AI interpretation with clinical insights.
              </p>
            </div>

            {/* Upload Section */}
            <div className="max-w-3xl mx-auto">
              <Card className="bg-zinc-900/50 border-zinc-800 overflow-hidden">
                <CardContent className="p-8">
                  {!file ? (
                    <div
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                      data-testid="lab-upload-dropzone"
                      className={`border-2 border-dashed rounded-xl p-12 text-center cursor-pointer transition-all border-emerald-800/50 ${
                        isDragging ? "border-emerald-500 bg-emerald-500/5" : "hover:border-emerald-700"
                      }`}
                      onClick={() => document.getElementById("lab-file-input").click()}
                    >
                      <input
                        id="lab-file-input"
                        type="file"
                        className="hidden"
                        accept="image/jpeg,image/png,image/webp,application/pdf"
                        onChange={(e) => handleFileSelect(e.target.files[0])}
                        data-testid="lab-file-input"
                      />
                      
                      <div className="flex flex-col items-center gap-4">
                        <div className="w-20 h-20 rounded-2xl bg-emerald-500/10 flex items-center justify-center border border-emerald-500/20">
                          <FlaskConical className="w-10 h-10 text-emerald-500" />
                        </div>
                        <div>
                          <p className="text-lg font-medium text-white mb-2">
                            Drop your lab report here
                          </p>
                          <p className="text-sm text-zinc-500">
                            or click to browse • JPEG, PNG, WEBP, PDF
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {/* File Preview */}
                      <div className="relative rounded-xl overflow-hidden bg-zinc-950 border border-zinc-800">
                        {preview ? (
                          <img
                            src={preview}
                            alt="Preview"
                            className="w-full max-h-[400px] object-contain"
                            data-testid="lab-image-preview"
                          />
                        ) : (
                          <div className="flex items-center justify-center py-20">
                            <div className="flex flex-col items-center gap-3 text-zinc-500">
                              <FileText className="w-16 h-16" />
                              <span className="font-medium">{file.name}</span>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* File Info */}
                      <div className="flex items-center justify-between p-4 bg-zinc-950 rounded-lg border border-zinc-800">
                        <div className="flex items-center gap-3">
                          <FileText className="w-5 h-5 text-emerald-500" />
                          <div>
                            <p className="text-sm font-medium text-white truncate max-w-xs">
                              {file.name}
                            </p>
                            <p className="text-xs text-zinc-500">
                              {(file.size / 1024 / 1024).toFixed(2)} MB
                            </p>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={clearFile}
                          disabled={isAnalyzing}
                          data-testid="clear-lab-file-btn"
                          className="text-zinc-400 hover:text-white"
                        >
                          Remove
                        </Button>
                      </div>

                      {/* Progress */}
                      {isAnalyzing && (
                        <div className="space-y-3" data-testid="lab-analysis-progress">
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-zinc-400">Analyzing report...</span>
                            <span className="text-emerald-500 font-medium">{Math.round(progress)}%</span>
                          </div>
                          <Progress value={progress} className="h-2 bg-zinc-800" />
                        </div>
                      )}

                      {/* Action Buttons */}
                      <div className="flex gap-4">
                        <Button
                          onClick={handleAnalyze}
                          disabled={isAnalyzing}
                          data-testid="analyze-lab-btn"
                          className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white font-medium py-6"
                        >
                          {isAnalyzing ? (
                            <>
                              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                              Analyzing...
                            </>
                          ) : (
                            <>
                              <FlaskConical className="w-5 h-5 mr-2" />
                              Analyze Report
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Supported Report Types */}
            <div className="mt-16">
              <h2 className="text-xl font-semibold text-white text-center mb-8" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Supported Report Types
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                {reportCategories.map((category, index) => (
                  <Card 
                    key={index} 
                    className="bg-zinc-900/30 border-zinc-800 hover:border-emerald-800/50 transition-colors"
                  >
                    <CardContent className="p-4 text-center">
                      <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center mx-auto mb-3">
                        <category.icon className="w-5 h-5 text-emerald-500" />
                      </div>
                      <h3 className="text-sm font-medium text-white mb-1">{category.label}</h3>
                      <p className="text-xs text-zinc-500 leading-relaxed">{category.examples}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Disclaimer */}
        <section className="py-8 border-t border-zinc-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-start gap-3 p-4 bg-zinc-900/50 rounded-lg border border-zinc-800">
              <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-zinc-400">
                <span className="font-medium text-zinc-300">Medical Disclaimer:</span> This AI tool is 
                intended for educational and informational purposes only. Always consult qualified 
                healthcare providers for medical decisions and interpretation of laboratory results.
              </p>
            </div>
          </div>
        </section>
      </div>
    </LabsLayout>
  );
}
